import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DynamicDialogRef } from '../../../../core/services/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DashboardService } from '../../../../core/services/dashboard/dashboard.service';
import {
  LayoutService,
  NavigationMode,
} from '../../../../core/services/layout/layout.service';
import { TranslationService } from '../../../../core/services/translation/translation.service';
import {
  ThemeName,
  ThemeSwitcherService,
} from '../../../../core/themes/theme-switcher.service';
import { TranslatePipe } from '../../../pipes/translate/translate.pipe';
import { DialogContentLayout } from '../../dialog/dialog-content-layout/dialog-content-layout';
import { IcCheckComponent } from '../../Icons/ic-check/ic-check';
import { IcInfoComponent } from '../../Icons/ic-info/ic-info';

/** Theme swatch shown in the settings picker. */
interface ThemeSwatch {
  /** Theme id persisted by {@link ThemeSwitcherService}. */
  id: ThemeName;
  /** Display name for the swatch. */
  name: string;
  /** Left half swatch color (hex without `#`). */
  start: string;
  /** Right half swatch color (hex without `#`). */
  end: string;
}

/** Language option shown in the theme-settings picker. */
interface LanguageOption {
  /** Translator language code used for activation (e.g. `zh`). */
  code: string;
  /** Display name for accessibility. */
  name: string;
  /** Short button label (e.g. `CN` for Chinese / `zh`). */
  label: string;
}

/**
 * ThemeSettingsComponent component.
 * Provides UI presentation and event handling for theme settings component.
 */
@Component({
  selector: 'app-theme-settings',
  standalone: true,
  imports: [
    CommonModule,
    DialogContentLayout,
    IcCheckComponent,
    IcInfoComponent,
    TranslatePipe,
    MatTooltipModule,
  ],
  templateUrl: './theme-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./theme-settings.component.scss'],
})
export class ThemeSettingsComponent {
  /** Injected service for dialog ref. */
  private dialogRef = inject(DynamicDialogRef);
  /** Injected service for theme service. */
  private themeService = inject(ThemeSwitcherService);
  /** Injected service for translation service. */
  private translationService = inject(TranslationService);
  /** Injected service for dashboard service. */
  private dashboardService = inject(DashboardService);
  /** Injected service for layout service. */
  public layoutService = inject(LayoutService);

  /** Languages from the Translator App download payload. */
  readonly supportedLanguages = this.translationService.supportedLanguages;

  /**
   * Language picker options — translator payload when available, with EN/NL/ZH ensured.
   * Chinese keeps translator code `zh` but shows label `CN` in the UI.
   */
  readonly languageOptions = computed((): LanguageOption[] => {
    const fromTranslator = this.supportedLanguages();
    const base =
      fromTranslator.length > 0
        ? fromTranslator.map((l) => ({
            code: l.code,
            name: l.name,
          }))
        : [
            { code: 'en', name: 'English' },
            { code: 'nl', name: 'Dutch' },
          ];

    const withZh = base.some((l) => l.code === 'zh')
      ? base
      : [...base, { code: 'zh', name: 'Chinese' }];

    return withZh.map((l) => ({
      code: l.code,
      name: l.name,
      label: l.code === 'zh' ? 'CN' : l.code.toUpperCase(),
    }));
  });

  /** Active UI language code (e.g. `en`, `nl`, `zh`). */
  selectedLanguage = signal(
    this.translationService.getCurrentLanguageCode()
  );

  /** Active theme — always mirrors the switcher service. */
  readonly selectedTheme = this.themeService.theme;

  /** Theme swatches available in the picker. */
  readonly themes: ThemeSwatch[] = [
    { id: 'primary', name: 'Primary', start: 'ECF9FF', end: 'BAE3E1' },
    { id: 'nexus', name: 'Nexus', start: 'D1E4FF', end: '56CCF2' },
    {
      id: 'carbon-slate',
      name: 'Carbon Slate',
      start: 'D1D5DB',
      end: '9CA3AF',
    },
    { id: 'dune', name: 'Dune', start: 'F5E8D8', end: 'C88242' },
  ];

  /** Property default dashboard. */
  defaultDashboard = computed(() => this.dashboardService.defaultDashboard());
  /** Property module landing page. */
  moduleLandingPage = computed(() => this.dashboardService.moduleLandingPage());

  /**
   * Switches the active UI language.
   * Deferred so impure `translate` bindings do not flip mid change-detection (NG0100).
   * @param langCode - Translator language code to activate (e.g. `en`, `nl`, `zh`)
   */
  selectLanguage(langCode: string): void {
    queueMicrotask(() => {
      this.translationService.setActiveLanguage(langCode);
      this.selectedLanguage.set(
        this.translationService.getCurrentLanguageCode()
      );
    });
  }

  /**
   * Applies and persists the selected theme.
   * @param themeId - Theme to activate
   */
  selectTheme(themeId: ThemeName): void {
    this.themeService.setTheme(themeId);
  }

  /**
   * Sets the default module dashboard preference.
   * @param dashboardId - Dashboard module id
   */
  setDefaultDashboard(dashboardId: string): void {
    this.dashboardService.setDefaultDashboard(dashboardId);
  }

  /**
   * Sets whether module entry opens the dashboard or list.
   * @param mode - Landing preference
   */
  setModuleLandingPage(mode: 'dashboard' | 'list'): void {
    this.dashboardService.setModuleLandingPage(mode);
  }

  /**
   * Applies navigation mode. Switching to/from top-bar remounts the header chrome
   * (header ↔ top-navigation). Closing this dialog first (with a deferred mode
   * handoff to the parent profile popup) avoids NG0100 from DynamicDialog being
   * destroyed mid change-detection.
   * @param mode - Navigation layout mode
   */
  selectNavigationMode(mode: NavigationMode): void {
    const current = this.layoutService.actualNavigationMode();
    if (current === mode) {
      return;
    }

    const remountsChrome = current === 'top-bar' || mode === 'top-bar';
    if (remountsChrome) {
      this.dialogRef.close({ applyNavigationMode: mode });
      return;
    }

    void this.layoutService.setNavigationMode(mode);
  }

  /** Closes the theme settings dialog. */
  close(): void {
    this.dialogRef.close();
  }
}
